import type { Node as ProseMirrorNode, ResolvedPos, Schema } from '@milkdown/kit/prose/model';
import { TextSelection } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';

import type { VariableContentChannel } from './config';
import { resolveChannelLabel } from './config';
import {
  CONTENT_VARIANT_NODE_NAME,
  DEFAULT_VARIANT_VALUE,
  EMPTY_DIMENSIONS_JSON,
  VARIABLE_CONTENT_DIRECTIVE_NAME,
  VARIABLE_CONTENT_NODE_NAME,
  VARIANT_DIRECTIVE_NAME,
} from './constants';
import { getChannelId, parseDimensions, setChannelId, stringifyDimensions } from './dimensions';

type MdastDirectiveLike = {
  type?: string;
  name?: string;
  children?: MdastDirectiveLike[];
};

function isContainerDirective<T extends MdastDirectiveLike>(node: T, name: string): boolean {
  return node.type === 'containerDirective' && node.name === name;
}

/**
 * Hoist nested `:::::variants` / `::::variant` bodies into the parent variant.
 * Keeps parse from trying to create `variable_content` inside `content_variant`.
 */
export function flattenVariantMarkdownChildren<T extends MdastDirectiveLike>(children: T[] | undefined): T[] {
  const out: T[] = [];

  for (const child of children ?? []) {
    if (!isContainerDirective(child, VARIABLE_CONTENT_DIRECTIVE_NAME)) {
      out.push(child);
      continue;
    }

    for (const nested of (child.children ?? []) as T[]) {
      if (isContainerDirective(nested, VARIANT_DIRECTIVE_NAME)) {
        out.push(...((nested.children ?? []) as T[]));
      } else {
        out.push(nested);
      }
    }
  }

  return out;
}

type ChannelSelectOption = {
  label: string;
  value: string;
};

/** Collect channel select values from content_variant children, optionally skipping one node. */
function collectUsedVariantValues(variableContent: ProseMirrorNode, exclude?: ProseMirrorNode | null): Set<string> {
  const used = new Set<string>();
  variableContent.forEach((child) => {
    if (child.type.name === CONTENT_VARIANT_NODE_NAME && child !== exclude) {
      used.add(getChannelId(parseDimensions(child.attrs.dimensions)));
    }
  });

  return used;
}

export function findAncestorVariableContent($pos: ResolvedPos): { depth: number; node: ProseMirrorNode } | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === VARIABLE_CONTENT_NODE_NAME) {
      return { depth, node };
    }
  }

  return null;
}

/**
 * Pick the visible variant for a variable_content block when `selectedChannelId` is set.
 * Exact channel match wins; otherwise falls back to the default variant.
 */
export function resolveChannelValueForVariableContent(
  variableContent: ProseMirrorNode,
  selectedChannelId: string,
): string | null {
  let defaultValue: string | null = null;
  let exactMatch: string | null = null;

  variableContent.forEach((child) => {
    if (child.type.name !== CONTENT_VARIANT_NODE_NAME) {
      return;
    }

    const channelValue = getChannelId(parseDimensions(child.attrs.dimensions));
    if (channelValue === DEFAULT_VARIANT_VALUE) {
      defaultValue = DEFAULT_VARIANT_VALUE;
    }
    if (channelValue === selectedChannelId) {
      exactMatch = channelValue;
    }
  });

  if (exactMatch != null) {
    return exactMatch;
  }

  return defaultValue;
}

/** Channel values already taken by sibling variants at `variantPos`. */
export function getSiblingChannelValues(view: EditorView, variantPos: number): Set<string> {
  const current = view.state.doc.nodeAt(variantPos);
  if (!current) {
    return new Set();
  }

  const ancestor = findAncestorVariableContent(view.state.doc.resolve(variantPos + 1));
  if (!ancestor) {
    return new Set();
  }

  return collectUsedVariantValues(ancestor.node, current);
}

/** Next unused channel select value, or `null` when default + all channels are taken. */
export function nextAvailableChannelValue(channels: Array<{ id: string }>, used: Set<string>): string | null {
  const candidates = [DEFAULT_VARIANT_VALUE, ...channels.map((channel) => channel.id)];

  return candidates.find((id) => !used.has(id)) ?? null;
}

export function canAddChannelVariant(channels: Array<{ id: string }>, variableContent: ProseMirrorNode): boolean {
  return nextAvailableChannelValue(channels, collectUsedVariantValues(variableContent)) != null;
}

/** Build a `content_variant` with an empty paragraph for the given channel select value. */
function createContentVariantNode(schema: Schema, value: string): ProseMirrorNode | null {
  const contentVariantType = schema.nodes[CONTENT_VARIANT_NODE_NAME];
  const paragraphType = schema.nodes.paragraph;
  if (!contentVariantType || !paragraphType) {
    return null;
  }

  return contentVariantType.create(
    {
      dimensions: value === DEFAULT_VARIANT_VALUE ? EMPTY_DIMENSIONS_JSON : stringifyDimensions(setChannelId(value)),
    },
    paragraphType.create(),
  );
}

/** Outer `variable_content` with a single default variant. */
export function createVariableContentBlock(schema: Schema): ProseMirrorNode | null {
  const variableContentType = schema.nodes[VARIABLE_CONTENT_NODE_NAME];
  const defaultVariant = createContentVariantNode(schema, DEFAULT_VARIANT_VALUE);
  if (!variableContentType || !defaultVariant) {
    return null;
  }

  return variableContentType.create(null, defaultVariant);
}

/** Dropdown options: Default + channels, minus siblings' values; always keep current. */
export function buildChannelSelectOptions(
  channels: VariableContentChannel[],
  currentValue: string,
  usedBySiblings: Set<string>,
): ChannelSelectOption[] {
  const options: ChannelSelectOption[] = [
    { label: 'Default', value: DEFAULT_VARIANT_VALUE },
    ...channels.map((channel) => ({ label: channel.name, value: channel.id })),
  ].filter((option) => option.value === currentValue || !usedBySiblings.has(option.value));

  if (!options.some((option) => option.value === currentValue)) {
    options.push({ label: resolveChannelLabel(channels, currentValue), value: currentValue });
  }

  return options;
}

/** Delete this variant, or the whole variable_content when it is the last child. */
export function deleteContentVariantAt(view: EditorView, variantPos: number): boolean {
  const variantNode = view.state.doc.nodeAt(variantPos);
  if (!variantNode) {
    return false;
  }

  const $inside = view.state.doc.resolve(variantPos + 1);
  const ancestor = findAncestorVariableContent($inside);
  if (!ancestor) {
    return false;
  }

  let tr = view.state.tr;
  if (ancestor.node.childCount <= 1) {
    const vcPos = $inside.before(ancestor.depth);
    tr = tr.delete(vcPos, vcPos + ancestor.node.nodeSize);
    // Prefer the block that preceded the wrapper (natural editing position).
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(vcPos), -1));
    view.dispatch(tr.scrollIntoView());
    view.focus();

    return true;
  }

  const variantIndex = $inside.index(ancestor.depth);
  tr = tr.delete(variantPos, variantPos + variantNode.nodeSize);
  if (variantIndex > 0) {
    // Positions before the deleted variant are unchanged — end of previous body.
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(variantPos - 1), -1));
  } else {
    // First sibling removed: land at the start of the (now first) remaining variant body.
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(variantPos + 1)));
  }
  view.dispatch(tr.scrollIntoView());
  view.focus();

  return true;
}

/**
 * Place the cursor in a content_variant body and focus the editor.
 * Call after any path that inserts a variant (Plus, slash, etc.).
 */
export function selectAndFocusContentVariantBody(view: EditorView, variantPos: number): boolean {
  const node = view.state.doc.nodeAt(variantPos);
  if (!node || node.type.name !== CONTENT_VARIANT_NODE_NAME) {
    return false;
  }

  const selection = TextSelection.near(view.state.doc.resolve(variantPos + 1));
  view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
  view.focus();

  return true;
}

/** Append the next unused channel variant at the end of a variable_content block. */
export function appendContentVariantAt(view: EditorView, variableContentPos: number, channels: Array<{ id: string }>): boolean {
  const variableContent = view.state.doc.nodeAt(variableContentPos);
  if (!variableContent || variableContent.type.name !== VARIABLE_CONTENT_NODE_NAME) {
    return false;
  }

  const value = nextAvailableChannelValue(channels, collectUsedVariantValues(variableContent));
  if (value == null) {
    return false;
  }

  const newVariant = createContentVariantNode(view.state.schema, value);
  if (!newVariant) {
    return false;
  }

  const insertPos = variableContentPos + variableContent.nodeSize - 1;
  view.dispatch(view.state.tr.insert(insertPos, newVariant).scrollIntoView());

  return selectAndFocusContentVariantBody(view, insertPos);
}
